import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const GoogleSearchConsoleSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [siteBrandMapping, setSiteBrandMapping] = useState({});

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');
  const brandId = urlParams.get('brand_id');

  useEffect(() => {
    if (!tempId) {
      setError('Missing temporary ID. Please restart the OAuth process.');
      setLoading(false);
      return;
    }

    fetchSites();
    fetchBrands();
  }, [tempId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/api/connectors/google/search-console/sites', {
        params: { temp_id: tempId }
      });

      if (response.data.success) {
        setSites(response.data.sites);
      } else {
        setError(response.data.message || 'Failed to fetch sites');
      }
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError(err.response?.data?.message || 'Failed to fetch Search Console sites');
    } finally {
      setLoading(false);
    }
  };

  const handleSiteSelection = (siteUrl, checked) => {
    if (checked) {
      const site = sites.find(s => s.siteUrl === siteUrl);
      setSelectedSites([...selectedSites, site]);
      // For already-connected sites, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth URL params.
      const defaultBrand = site?.alreadyConnected && site?.connectedBrandId
        ? String(site.connectedBrandId)
        : brandId;
      setSiteBrandMapping(prev => ({
        ...prev,
        [siteUrl]: defaultBrand
      }));
    } else {
      setSelectedSites(selectedSites.filter(s => s.siteUrl !== siteUrl));
      // Remove brand mapping
      setSiteBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[siteUrl];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (siteUrl, newBrandId) => {
    setSiteBrandMapping(prev => ({
      ...prev,
      [siteUrl]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedSites.length === 0) {
      setError('Please select at least one site to connect');
      return;
    }

    // Validate brands
    const sitesWithoutBrands = selectedSites.filter(site => 
      !siteBrandMapping[site.siteUrl]
    );
    if (sitesWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected sites`);
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const site of selectedSites) {
        const selectedBrandId = siteBrandMapping[site.siteUrl] || brandId;
        const response = await api.post('/api/connectors/google/search-console/create', {
          temp_id: tempId,
          brand_id: parseInt(selectedBrandId),
          site_url: site.siteUrl,
          site_permission: site.permissionLevel
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${site.siteUrl}: ${response.data.message}`);
        }
      }

      // Get unique brand names
      const uniqueBrands = [...new Set(selectedSites.map(site => 
        getBrandName(siteBrandMapping[site.siteUrl] || brandId)
      ))];

      navigate('/connectors', { 
        state: { 
          message: `Successfully created ${createdConnectors.length} Google Search Console connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          type: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'siteOwner': return 'success';
      case 'siteFullUser': return 'primary';
      case 'siteRestrictedUser': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Search Console sites...</Typography>
      </Box>
    );
  }

  if (error && sites.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>
          Back to Connectors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <SearchIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Select Google Search Console Sites
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose one or more Search Console sites to connect. You can assign each site to a different brand.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {sites.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Search Console sites found. Make sure you have verified sites in your Google Search Console account.
        </Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Available Sites ({sites.length})
          </Typography>

          <Grid container spacing={2}>
            {sites.map((site) => (
              <Grid item xs={12} md={6} key={site.siteUrl}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSites.some(s => s.siteUrl === site.siteUrl)}
                          onChange={(e) => handleSiteSelection(site.siteUrl, e.target.checked)}
                          disabled={creating}
                        />
                      }
                      label={
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                            {site.siteUrl}
                          </Typography>
                          {site.alreadyConnected && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={site.connectedBrandName
                                  ? `Connected · ${site.connectedBrandName}`
                                  : 'Already connected'}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          )}
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Chip 
                            label={site.permissionLevel} 
                            size="small" 
                            color={getPermissionColor(site.permissionLevel)}
                            sx={{ mt: 1 }}
                          />

                          {/* Brand Selection */}
                          {selectedSites.some(s => s.siteUrl === site.siteUrl) && (
                            <Box sx={{ mt: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`brand-select-${site.siteUrl}`}>Assign to Brand</InputLabel>
                                <Select
                                  labelId={`brand-select-${site.siteUrl}`}
                                  value={siteBrandMapping[site.siteUrl] || ''}
                                  label="Assign to Brand"
                                  onChange={(e) => handleBrandChange(site.siteUrl, e.target.value)}
                                  disabled={creating}
                                  sx={{ borderRadius: 1 }}
                                >
                                  {brands.map(brand => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedSites.length} site(s) selected
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/connectors')}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateConnectors}
                disabled={selectedSites.length === 0 || creating}
                startIcon={creating ? <CircularProgress size={20} /> : null}
              >
                {creating ? 'Creating...' : `Create ${selectedSites.length} Connector(s)`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default GoogleSearchConsoleSelection;